import { PollResponse } from 'api/api.types';

// define cases where we don't want to show a party that won a seat in the previous election and did not compete in the current one
export const excludedPrevParties = [
  {
    // CVP seats are counted for the party DieMitte. Therefore we do not want to show the CVP again
    yearOfCurrentElection: 2023,
    listIdPreview: 302,
    listIdProd: 800,
  },
];

// Preview-Party-ID: Prod-Party-ID
const PARTY_IDS_PROD = {
  299: 805,
  300: 806,
  301: 802,
  302: 800,
  303: 803,
  304: 804,
  305: 428,
  306: 801,
  307: 810,
  308: 808,
  309: 811,
  321: 896,
};

// Preview-Party-ID: Prod-Party-ID
const PARTY_IDS_PROD_GRW = {
  299: 805,
  300: 806,
  301: 802,
  302: 896,
  303: 803,
  304: 804,
  305: 428,
  306: 801,
  307: 810,
  309: 811,
  320: 902,
};

const transformPartyMerger2019 = (data: PollResponse, apiUrl: string) => {
  const isProdUrl = !apiUrl.includes('public-preview');
  const { bdpId, cvpId } = isProdUrl
    ? {
        bdpId: PARTY_IDS_PROD[305],
        cvpId: PARTY_IDS_PROD[302],
      }
    : {
        bdpId: 305,
        cvpId: 302,
      };

  const bdp = data.data.parties.find((party) => party.id === bdpId);
  const cvp = data.data.parties.find((party) => party.id === cvpId);
  const dieMitte = {
    id: isProdUrl ? PARTY_IDS_PROD[321] : 321,
    color: '',
    title: {
      de: 'DieMitte.Miteinander.Für den Aargau.',
    },
    short_title: {
      de: 'DieMitte',
    },
    results: {
      additional_votes: bdp.results.additional_votes + cvp.results.additional_votes,
      candidate_votes: bdp.results.candidate_votes + cvp.results.candidate_votes,
      changed_ballots: bdp.results.changed_ballots + cvp.results.changed_ballots,
      party_votes_percentage:
        bdp.results.party_votes_percentage + cvp.results.party_votes_percentage,
      party_votes: bdp.results.party_votes + cvp.results.party_votes,
      total_ballots: bdp.results.total_ballots + cvp.results.total_ballots,
      unchanged_ballots: bdp.results.unchanged_ballots + cvp.results.unchanged_ballots,
      votes_from_changed: bdp.results.votes_from_changed + cvp.results.votes_from_changed,
      votes_from_unchanged:
        bdp.results.votes_from_unchanged + cvp.results.votes_from_unchanged,
      seat_claim: bdp.results.seat_claim + cvp.results.seat_claim,
      voters_count: bdp.results.voters_count + cvp.results.voters_count,
      voters_count_percentage:
        bdp.results.voters_count_percentage + cvp.results.voters_count_percentage,
    },
  };

  return {
    ...data,
    data: {
      ...data.data,
      parties: [...data.data.parties, dieMitte],
    },
  };
};

// Party IDs in prod api are different from the ones in the archive api [1]
const transformArchivePartyIds = (data: PollResponse) => {
  const transformedParties = data.data.parties.map((party) => {
    const partyId = PARTY_IDS_PROD[party.id] || party.id;
    return {
      ...party,
      id: partyId,
    };
  });

  return {
    ...data,
    data: {
      ...data.data,
      parties: transformedParties,
    },
  };
};

// [1]
const transformArchivePartyIdsGRW = (data: PollResponse) => {
  const transformedParties = data.data.list_groups.map((list_group) => {
    const partyId = PARTY_IDS_PROD_GRW[list_group.party_id] || list_group.id;
    return {
      ...list_group,
      party_id: partyId,
    };
  });

  return {
    ...data,
    data: {
      ...data.data,
      list_groups: transformedParties,
    },
  };
};

const transformations = [
  {
    condition: (data: PollResponse, isPrevResult: boolean) => {
      const is2019NrwInPreview = data.data.info.id.toString() === '126';
      const is2019NrwInProd =
        data.data.info.type === 'election_proportional_apportionment' &&
        data.data.info.date === '2019-10-20';
      return isPrevResult && (is2019NrwInPreview || is2019NrwInProd);
    },
    transformation: transformPartyMerger2019,
  },
  {
    condition: (data: PollResponse, isPrevResult: boolean, apiUrl: string) => {
      const isProdUrl = !apiUrl.includes('public-preview');
      const isArchiveNRW = data.data.info.id.toString() === '201510183';
      return isPrevResult && isProdUrl && isArchiveNRW;
    },
    transformation: transformArchivePartyIds,
  },
  {
    condition: (data: PollResponse, isPrevResult: boolean, apiUrl: string) => {
      const isProdUrl = !apiUrl.includes('public-preview');
      const isArchiveGRW = data.data.info.id.toString() === '2016102301';
      return isPrevResult && isProdUrl && isArchiveGRW;
    },
    transformation: transformArchivePartyIdsGRW,
  },
];

const transformPoll = (data: PollResponse, isPrevResult: boolean, apiUrl: string) => {
  let transformedData = data;

  transformations.forEach((transformation) => {
    if (!transformation.condition(data, isPrevResult, apiUrl)) return;
    transformedData = transformation.transformation(transformedData, apiUrl);
  });

  return transformedData;
};

export default transformPoll;
